import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  CircularProgress,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Slider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Church as ChurchIcon,
  Group as CommunityIcon,
  FamilyRestroom as GotraIcon
} from '@mui/icons-material';
import api from '../../services/api';
import toast from 'react-hot-toast';

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const ReligionManager = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [religions, setReligions] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [gotras, setGotras] = useState([]);
  const [filteredCommunities, setFilteredCommunities] = useState([]);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [currentType, setCurrentType] = useState('religion');
  const [formData, setFormData] = useState({
    name: '',
    gotra_name: '',
    description: '',
    display_order: 0,
    religion_id: '',
    is_active: 1
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [religionFilter, setReligionFilter] = useState('all');

  useEffect(() => {
    fetchReligions();
    fetchCommunities();
    fetchGotras();
  }, []);

  const fetchReligions = async () => {
    try {
      const response = await api.get('/religion/religions');
      setReligions(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch religions');
    }
  };

  const fetchCommunities = async (religionId = null) => {
    try {
      const url = religionId ? `/religion/communities?religion_id=${religionId}` : '/religion/communities';
      const response = await api.get(url);
      setCommunities(response.data.data);
      if (religionId) {
        setFilteredCommunities(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch communities');
    }
  };

  const fetchGotras = async () => {
    try {
      const response = await api.get('/religion/gotras');
      setGotras(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch gotras');
    }
  };

  const handleOpenDialog = (type, item = null) => {
    setCurrentType(type);
    setEditingItem(item);
    
    if (item) {
      if (type === 'religion') {
        setFormData({
          name: item.name || '',
          description: item.description || '',
          display_order: item.display_order || 0,
          is_active: item.is_active === 1
        });
      } else if (type === 'community') {
        setFormData({
          name: item.name || '',
          description: item.description || '',
          religion_id: item.religion_id || '',
          is_active: item.is_active === 1
        });
      } else if (type === 'gotra') {
        setFormData({
          gotra_name: item.gotra_name || '',
          description: item.description || '',
          is_active: item.is_active === 1
        });
      }
    } else {
      setFormData({
        name: '',
        gotra_name: '',
        description: '',
        display_order: 0,
        religion_id: '',
        is_active: 1
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (currentType === 'religion' && !formData.name) {
      toast.error('Religion name is required');
      return;
    }
    if (currentType === 'community' && (!formData.religion_id || !formData.name)) {
      toast.error('Religion and community name are required');
      return;
    }
    if (currentType === 'gotra' && !formData.gotra_name) {
      toast.error('Gotra name is required');
      return;
    }

    setLoading(true);
    try {
      let response;
      let url;
      
      if (currentType === 'religion') {
        url = '/religion/religions';
      } else if (currentType === 'community') {
        url = '/religion/communities';
      } else {
        url = '/religion/gotras';
      }
      
      let payload;
      if (currentType === 'religion') {
        payload = {
          name: formData.name,
          description: formData.description,
          display_order: formData.display_order,
          is_active: formData.is_active ? 1 : 0
        };
      } else if (currentType === 'community') {
        payload = {
          religion_id: formData.religion_id,
          name: formData.name,
          description: formData.description,
          is_active: formData.is_active ? 1 : 0
        };
      } else {
        payload = {
          gotra_name: formData.gotra_name,
          description: formData.description,
          is_active: formData.is_active ? 1 : 0
        };
      }
      
      if (editingItem) {
        response = await api.put(`${url}/${editingItem.id}`, payload);
      } else {
        response = await api.post(url, payload);
      }
      
      if (response.data.success) {
        const typeNames = {
          religion: 'Religion',
          community: 'Community',
          gotra: 'Gotra'
        };
        toast.success(`${typeNames[currentType]} ${editingItem ? 'updated' : 'added'} successfully`);
        setDialogOpen(false);
        
        // Reset form
        setFormData({
          name: '',
          gotra_name: '',
          description: '',
          display_order: 0,
          religion_id: '',
          is_active: 1
        });
        
        // Refresh data
        if (currentType === 'religion') {
          fetchReligions();
        } else if (currentType === 'community') {
          fetchCommunities();
        } else {
          fetchGotras();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to save`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type, id, name) => {
    if (window.confirm(`Are you sure you want to delete ${name}?`)) {
      try {
        let url;
        if (type === 'religion') {
          url = `/religion/religions/${id}`;
        } else if (type === 'community') {
          url = `/religion/communities/${id}`;
        } else {
          url = `/religion/gotras/${id}`;
        }
        
        const response = await api.delete(url);
        if (response.data.success) {
          const typeNames = {
            religion: 'Religion',
            community: 'Community',
            gotra: 'Gotra'
          };
          toast.success(`${typeNames[type]} deleted successfully`);
          
          if (type === 'religion') {
            fetchReligions();
          } else if (type === 'community') {
            fetchCommunities();
          } else {
            fetchGotras();
          }
        }
      } catch (error) {
        toast.error(error.response?.data?.message || `Failed to delete`);
      }
    }
  };

  const handleReligionFilter = async (religionId) => {
    setReligionFilter(religionId);
    if (religionId === 'all') {
      await fetchCommunities();
    } else {
      await fetchCommunities(religionId);
    }
  };

  const getReligionName = (religionId) => {
    const religion = religions.find(r => r.id == religionId);
    return religion ? religion.name : 'N/A';
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" mb={3}>
        Religion & Community Management
      </Typography>

      <Paper>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab icon={<ChurchIcon />} label="Religions" />
          <Tab icon={<CommunityIcon />} label="Communities" />
          <Tab icon={<GotraIcon />} label="Gotras" />
        </Tabs>

        {/* Religions Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <TextField
              size="small"
              placeholder="Search religions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ width: 300 }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('religion')}
              sx={{
                background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)'
              }}
            >
              Add Religion
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Display Order</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {religions
                  .filter(r => r.name.toLowerCase().includes(searchTerm.toLowerCase()))
                  .map((religion) => (
                    <TableRow key={religion.id} hover>
                      <TableCell>
                        <Typography fontWeight="500">{religion.name}</Typography>
                      </TableCell>
                      <TableCell>{religion.description || '-'}</TableCell>
                      <TableCell>{religion.display_order}</TableCell>
                      <TableCell>
                        <Chip 
                          label={religion.is_active ? 'Active' : 'Inactive'}
                          color={religion.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleOpenDialog('religion', religion)} color="primary">
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete('religion', religion.id, religion.name)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {religions.length === 0 && !loading && (
            <Box textAlign="center" py={5}>
              <Typography color="textSecondary">No religions found. Click "Add Religion" to create one.</Typography>
            </Box>
          )}
        </TabPanel>

        {/* Communities Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box mb={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search communities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Religion</InputLabel>
                  <Select
                    value={religionFilter}
                    label="Filter by Religion"
                    onChange={(e) => handleReligionFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Religions</MenuItem>
                    {religions.map(religion => (
                      <MenuItem key={religion.id} value={religion.id}>{religion.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog('community')}
                  sx={{
                    background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)'
                  }}
                >
                  Add Community
                </Button>
              </Grid>
            </Grid>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Religion</TableCell>
                  <TableCell>Community Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {communities
                  .filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()))
                  .map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Chip 
                          label={getReligionName(item.religion_id)} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="500">{item.name}</Typography>
                      </TableCell>
                      <TableCell>{item.description || '-'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={item.is_active ? 'Active' : 'Inactive'}
                          color={item.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleOpenDialog('community', item)} color="primary">
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete('community', item.id, item.name)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {communities.length === 0 && !loading && (
            <Box textAlign="center" py={5}>
              <Typography color="textSecondary">No communities found. Click "Add Community" to create one.</Typography>
            </Box>
          )}
        </TabPanel>

        {/* Gotras Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <TextField
              size="small"
              placeholder="Search gotras..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ width: 300 }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('gotra')}
              sx={{
                background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)'
              }}
            >
              Add Gotra
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Gotra Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gotras
                  .filter(g => g.gotra_name.toLowerCase().includes(searchTerm.toLowerCase()))
                  .map((gotra) => (
                    <TableRow key={gotra.id} hover>
                      <TableCell>
                        <Typography fontWeight="500">{gotra.gotra_name}</Typography>
                      </TableCell>
                      <TableCell>{gotra.description || '-'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={gotra.is_active ? 'Active' : 'Inactive'}
                          color={gotra.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleOpenDialog('gotra', gotra)} color="primary">
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete('gotra', gotra.id, gotra.gotra_name)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {gotras.length === 0 && !loading && (
            <Box textAlign="center" py={5}>
              <Typography color="textSecondary">No gotras found. Click "Add Gotra" to create one.</Typography>
            </Box>
          )}
        </TabPanel>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)', 
          color: '#fff'
        }}>
          {editingItem ? 'Edit' : 'Add'} {
            currentType === 'religion' ? 'Religion' : 
            currentType === 'community' ? 'Community' : 'Gotra'
          }
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {currentType === 'community' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Religion</InputLabel>
                  <Select
                    value={formData.religion_id}
                    label="Religion"
                    onChange={(e) => setFormData({ ...formData, religion_id: e.target.value })}
                    required
                  >
                    {religions.map(religion => (
                      <MenuItem key={religion.id} value={religion.id}>{religion.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            {currentType === 'religion' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Religion Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Hindu, Buddhist, Muslim, Christian"
                />
              </Grid>
            )}
            
            {currentType === 'community' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Community Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Brahmin, Chhetri, Newar, Gurung"
                />
              </Grid>
            )}
            
            {currentType === 'gotra' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Gotra Name"
                  value={formData.gotra_name}
                  onChange={(e) => setFormData({ ...formData, gotra_name: e.target.value })}
                  required
                  placeholder="e.g., Bashista, Bharadwaj, Gautam, Kashyap"
                />
              </Grid>
            )}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
              />
            </Grid>
            
            {currentType === 'religion' && (
              <Grid item xs={12}>
                <Typography gutterBottom>Display Order</Typography>
                <Slider
                  value={formData.display_order}
                  onChange={(e, newValue) => setFormData({ ...formData, display_order: newValue })}
                  min={0}
                  max={100}
                  valueLabelDisplay="auto"
                  marks={[
                    { value: 0, label: '0' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' },
                    { value: 75, label: '75' },
                    { value: 100, label: '100' }
                  ]}
                />
              </Grid>
            )}
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active === 1 || formData.is_active === true}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={loading}
            sx={{
              background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)'
            }}
          >
            {loading ? <CircularProgress size={24} /> : (editingItem ? 'Update' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReligionManager;